import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Building } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";
import AuthLayout from "@/components/auth/AuthLayout";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { handleSubscriptionUpgrade } from "@/lib/subscriptionUtils";

const formSchema = z.object({
  organisationName: z.string().min(2, "Organisation name must be at least 2 characters"),
  acceptTerms: z.boolean().refine((val) => val === true, {
    message: "You must accept the Terms & Privacy Policy to continue",
  }),
});

type FormData = z.infer<typeof formSchema>;

const GoogleSignUpComplete = () => {
  const { completeGoogleSignup, isLoading } = useAuth();
  const navigate = useNavigate();
  
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      organisationName: "",
      acceptTerms: false,
    },
  });
  
  const onSubmit = async (data: FormData) => {
    try {
      await completeGoogleSignup(data.organisationName);
      
      // Handle subscription upgrade if selected during signup
      const selectedSubscription = localStorage.getItem('selected-subscription');
      if (selectedSubscription) {
        try {
          await handleSubscriptionUpgrade();
          // If handleSubscriptionUpgrade redirects to Stripe, this won't execute
          // If it doesn't redirect (e.g., Free plan), continue to dashboard
        } catch (error) {
          console.error('Error handling subscription upgrade:', error);
          // Continue to dashboard even if subscription upgrade fails
          navigate("/");
        }
      } else {
        navigate("/");
      }
    } catch (error) {
      console.error("Google sign-up completion error:", error);
      // Toast already handled in auth context
    }
  };
  
  return (
    <AuthLayout
      title="Complete your sign up"
      subtitle="Please provide your organisation name to complete the sign up"
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="organisationName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium">Organisation Name</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Building className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Acme Corp"
                      className="pl-10 h-11"
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="acceptTerms"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="text-sm font-normal">
                    I accept the{" "}
                    <a
                      href="https://www.recallio.ai/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline font-medium"
                    >
                      Terms & Privacy Policy
                    </a>
                  </FormLabel>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
          
          <Button type="submit" className="w-full h-11 text-base" disabled={isLoading}>
            {isLoading ? "Completing sign up..." : "Complete sign up"}
          </Button>
        </form>
      </Form>
    </AuthLayout>
  );
};

export default GoogleSignUpComplete; 